import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { MaskedRange } from 'imask';
import { MinusCircle, PlusCircle } from 'react-feather';
import { Box, Button, Checkbox, Chip, ControlledButtonGroup, findBy, FormControlLabel, Grid, makeStyles, MenuItem, QuestionVariants, Select, SurveyChecklist, SurveyDateField, SurveyNumberField, SurveySlider, SurveyStringField, } from '../';
import SurveySwitch from '../SurveySwitch';
const useStyles = makeStyles((theme) => ({
    root: {},
    buttonGroup: {
        marginTop: theme.spacing(1.2),
        [theme.breakpoints.down('md')]: {
            margin: theme.spacing(2),
            width: '88%',
        },
        // This is important for when we're showing a selected button
        // that doesn't have an :active state. To reproduce, answer
        // a choice-standard/long question, progress to the next question,
        // then go back to the one you just answered.
        '& > .Button-selected': {
            color: theme.palette.common.white,
            backgroundColor: theme.palette.primary.main,
        },
    },
    flatButtonGroup: {
        boxShadow: 'none',
        '& > .Button-selected': {
            color: theme.palette.common.white,
            backgroundColor: theme.palette.primary.main,
        },
    },
    flatButtonGroupButton: {
        fontWeight: 'bold',
        minHeight: 56,
        color: theme.palette.primary.main,
        backgroundColor: 'rgb(245, 245, 248)',
    },
    button: {
        fontWeight: 'bold',
        minHeight: 56,
        color: theme.palette.primary.main,
    },
    dropdownMenu: {
        maxHeight: 'calc(75% - 96px)',
    },
    dropdownItem: {
        whiteSpace: 'inherit',
    },
    dropdownItemCentered: {
        whiteSpace: 'inherit',
        justifyContent: 'center',
        backgroundColor: '#e3e3e3',
        fontWeight: 'bold',
        color: theme.palette.primary.main,
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        '& .MuiChip-label': {
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            [theme.breakpoints.down('md')]: {
                maxWidth: '55vw',
            },
        },
    },
    chip: {
        margin: 2,
    },
    checkboxForm: {
        margin: 0,
        '& svg': {
            maxWidth: '100%',
        },
    },
    collapsibleContainer: {
        marginBottom: theme.spacing(2),
        textAlign: 'right',
    },
    collapsibleInjectionsContainer: {
        display: 'block',
        width: '100%',
    },
    collapsibleInjections: {
        color: '#2396CF',
        minHeight: '30px',
        padding: 0,
        fontSize: '16px',
        fontWeight: 400,
        '&:hover': {
            opacity: '0.7',
        },
    },
    collapsibleRemove: {
        color: '#767676',
    },
    collapsibleGeneral: {
        color: '#2396CF',
        border: '1px solid #2396CF',
        borderRadius: '8px',
        fontSize: '16px',
        minHeight: '30px',
        fontWeight: 700,
        boxShadow: 'none',
        padding: theme.spacing(1, 2),
        '&:hover': {
            opacity: '0.7',
            border: '1px solid #2396CF',
        },
    },
}), { name: 'SurveyItemQuestion' });
/**
 * TODO: Refactor many of the question variant components to specific Survey sub-components
 */
const SurveyItemQuestion = ({ item, answer = [], onAnswer, defaultIcon, checkedIcon, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v;
    const classes = useStyles();
    const data = typeof item.data === 'string' ? JSON.parse(item.data) : item.data;
    useEffect(() => {
        var _a;
        if ((answer === undefined || answer.length === 0) &&
            (item.initialValue !== undefined || data.initialValue !== undefined)) {
            const val = (_a = item.initialValue) !== null && _a !== void 0 ? _a : data.initialValue;
            if (val !== undefined) {
                onAnswer(item, [String(val)]); // Call with initial value
            }
        }
        // Add dependencies if needed
        // eslint-disable-next-line
    }, []);
    const baseClasses = clsx('SurveyItemQuestion-root', classes.root);
    const [openMultiselect, setOpenMultiselect] = useState(false);
    const handleMultiselectOpen = () => {
        setOpenMultiselect(true);
    };
    const handleMultiselectClose = () => {
        setOpenMultiselect(false);
    };
    if (item.custom && item.custom.component) {
        const Component = item.custom.component;
        return (_jsx(Component, { className: baseClasses, answer: answer, onAnswer: onAnswer }));
    }
    switch (item.variant) {
        case QuestionVariants.choiceStandard: {
            return (_jsx(ControlledButtonGroup, Object.assign({ id: item.slug.toLowerCase(), onButtonSelect: (id) => onAnswer(item, id), orientation: (_a = item.custom) === null || _a === void 0 ? void 0 : _a.orientation, className: clsx(baseClasses, classes.buttonGroup) }, { children: item.data.answerOptions.map((opt) => {
                    const isSelected = !!answer.find((v) => {
                        // Handle both string and number comparisons for button groups
                        // Also handle decimal vs integer comparison (e.g., "0" vs "0.0")
                        const normalizedAnswer = String(Number(v));
                        const normalizedOption = String(Number(opt.value));
                        const match = normalizedAnswer === normalizedOption;
                        return match;
                    });
                    return (_jsx(Button, Object.assign({ className: classes.button, id: String(opt.value), fullWidth: true, size: "large", variant: "outlined", selected: isSelected }, { children: opt.label }), opt.value));
                }) })));
        }
        case QuestionVariants.choiceLong:
            return (_jsx(SurveyChecklist, { id: item.slug.toLowerCase(), className: baseClasses, items: item.data.answerOptions, onItemSelect: (value) => onAnswer(item, value) }));
        case QuestionVariants.choiceFlat: {
            const options = item.data.answerOptions;
            return (_jsx(ControlledButtonGroup, Object.assign({ id: item.slug.toLowerCase(), onButtonSelect: (id) => onAnswer(item, id), className: clsx(baseClasses, classes.flatButtonGroup) }, { children: options.map((opt) => (_jsx(Button, Object.assign({ className: clsx(classes.flatButtonGroupButton), id: String(opt.value), size: "large", variant: "outlined", selected: answer.length > 0
                        ? answer.some((v) => String(Number(v)) === String(Number(opt.value)))
                        : undefined }, { children: opt.label }), opt.value))) })));
        }
        case QuestionVariants.choiceToggle: {
            const option = item.data.answerOptions.find((opt) => !answer.includes(opt.value));
            return (_jsx(Button, Object.assign({ id: item.slug.toLowerCase(), className: baseClasses, fullWidth: true, variant: "outlined" }, { children: option === null || option === void 0 ? void 0 : option.label })));
        }
        case QuestionVariants.choiceSwitch:
            return (_jsx(SurveySwitch, { value: String((_c = (_b = answer === null || answer === void 0 ? void 0 : answer[0]) !== null && _b !== void 0 ? _b : item.initialValue) !== null && _c !== void 0 ? _c : '0.0'), onChange: (val) => onAnswer(item, val), name: item.slug, minLabel: item.data.minLabel || 'Off', maxLabel: item.data.maxLabel || 'On' }));
        case QuestionVariants.slider:
        case QuestionVariants.sliderHorizontal: {
            const sliderValue = answer[0] != null ? Number(answer[0]) : null;
            // TODO: Migrate SurveySlider to an API where it accepts an `item` prop
            // instead of each individual attribute
            return (_jsx(SurveySlider, { id: item.slug.toLowerCase(), className: baseClasses, onChange: (val) => {
                    onAnswer(item, val);
                }, value: sliderValue, labelMaxValue: item.data.maxLabel, labelMinValue: item.data.minLabel, rangeMin: Number(item.data.minValue), rangeMax: Number(item.data.maxValue), initialValue: Number(item.initialValue), vertical: item.variant == QuestionVariants.slider }));
        }
        case QuestionVariants.choiceDropdown: {
            const options = item.data.answerOptions;
            let selectedOptions = options
                .filter((opt) => answer.includes(opt.value))
                .map((o) => o.value);
            const noneValue = 'None';
            const noneSelection = options.filter((opt) => answer.includes(opt.value) && opt.label === noneValue);
            const isNoneSelected = noneSelection.length > 0;
            if (isNoneSelected)
                selectedOptions = noneSelection.map((o) => o.value);
            const sharedProps = {
                className: clsx('SurveyItemQuestion-root', 'SurveyItemQuestion-select', classes.root),
                options: options.map((opt) => (_jsxs(MenuItem, Object.assign({ className: classes.dropdownItem, value: opt.value, disabled: isNoneSelected && opt.label !== noneValue }, { children: [item.data.repeat ? (_jsx(Checkbox, { checked: selectedOptions.indexOf(opt.value) > -1 })) : null, opt.label] }), opt.label))),
                MenuProps: {
                    classes: {
                        paper: classes.dropdownMenu,
                    },
                },
            };
            // TODO Refactor this code to new Survey sub-components
            if (item.data.repeat) {
                const selectedOptions = options
                    .filter((opt) => answer.includes(opt.value))
                    .map((o) => o.value);
                const handleDeleteChip = (value) => {
                    const nextAnswers = selectedOptions.filter((opt) => opt !== value);
                    onAnswer(item, nextAnswers);
                };
                return (_jsxs(Select, Object.assign({ id: item.slug.toLowerCase(), multiple: true, className: sharedProps.className, value: selectedOptions, MenuProps: sharedProps.MenuProps, renderValue: (selected) => (_jsx("div", Object.assign({ className: classes.chips }, { children: selected.map((value) => {
                            var _a;
                            return (_jsx(Chip, { label: ((_a = findBy('value', value, options)) === null || _a === void 0 ? void 0 : _a.label) || '', className: classes.chip, onDelete: () => handleDeleteChip(value), onMouseDown: (event) => {
                                    event.stopPropagation();
                                } }, value));
                        }) }))), onChange: (ev) => {
                        const value = ev.target.value;
                        let asOptions = options.filter((opt) => value.includes(opt.value));
                        const noneSelection = options.filter((opt) => value.includes(opt.value) && opt.label === noneValue);
                        const isNoneSelected = noneSelection.length > 0;
                        if (isNoneSelected)
                            asOptions = noneSelection;
                        if (!asOptions.length) {
                            onAnswer(item, []);
                        }
                        else {
                            onAnswer(item, asOptions.map((o) => o.value));
                        }
                    }, open: openMultiselect, onClose: handleMultiselectClose, onOpen: handleMultiselectOpen }, { children: [_jsx(MenuItem, Object.assign({ className: classes.dropdownItemCentered, onClick: () => {
                                setOpenMultiselect(false);
                            } }, { children: "Close" })), sharedProps.options] })));
            }
            else {
                const selectedOption = ((_d = options.find((opt) => answer.includes(opt.value))) === null || _d === void 0 ? void 0 : _d.value) || '';
                // If there is only one option and the question is required, auto-set the value
                if (item.required && options.length === 1 && !selectedOption) {
                    onAnswer(item, [options[0].value]);
                }
                return (_jsx(Select, Object.assign({ id: item.slug.toLowerCase(), className: sharedProps.className, value: selectedOption, MenuProps: sharedProps.MenuProps, onChange: (ev) => {
                        const value = ev.target.value;
                        const asOption = options.find((opt) => value === opt.value);
                        if (!asOption) {
                            onAnswer(item, []);
                        }
                        else {
                            onAnswer(item, [asOption.value]);
                        }
                    } }, { children: sharedProps.options })));
            }
        }
        case QuestionVariants.choiceCollapse: {
            const options = item.data.answerOptions;
            // TODO Refactor this code to new Survey sub-components
            const selectedOption = ((_e = options.find((opt) => answer.includes(opt.value))) === null || _e === void 0 ? void 0 : _e.value) || '';
            // If there is only one option and the question is required, auto-set the value
            if (!selectedOption) {
                onAnswer(item, ['0.0']);
            }
            const isInjectionSite = item.data.prompt === 'New Injection Site';
            const displayButtonText = selectedOption === '0.0'
                ? `Add ${item.data.prompt}`
                : isInjectionSite
                    ? 'Remove'
                    : `Remove ${item.data.prompt}`;
            return (_jsx(Box, Object.assign({ className: clsx(classes.collapsibleContainer, {
                    [classes.collapsibleInjectionsContainer]: isInjectionSite,
                }) }, { children: _jsx(Button, Object.assign({ id: item.slug.toLowerCase(), className: clsx({
                        [classes.collapsibleInjections]: isInjectionSite,
                        [classes.collapsibleGeneral]: !isInjectionSite,
                        [classes.collapsibleRemove]: isInjectionSite && selectedOption === '1.0',
                    }), variant: isInjectionSite ? 'text' : 'outlined', startIcon: isInjectionSite ? null : selectedOption === '0.0' ? (_jsx(PlusCircle, {})) : (_jsx(MinusCircle, {})), onClick: () => {
                        if (selectedOption === '1.0') {
                            onAnswer(item, ['0.0']);
                        }
                        else {
                            onAnswer(item, ['1.0']);
                        }
                    } }, { children: displayButtonText })) })));
        }
        case QuestionVariants.choiceIcon: {
            const options = item.data.answerOptions;
            const rowColumns = item.data.maxValue || 3;
            let selectedOptions = options
                .filter((opt) => answer.includes(opt.value))
                .map((o) => o.value);
            const noneValue = 'None';
            const noneSelection = options.filter((opt) => answer.includes(opt.value) && opt.label === noneValue);
            const isNoneSelected = noneSelection.length > 0;
            if (isNoneSelected)
                selectedOptions = noneSelection.map((o) => o.value);
            const altOptions = [...options];
            const displayArray = Array.from({ length: Math.ceil(options.length / rowColumns) }, () => altOptions.splice(0, rowColumns));
            // TODO Refactor this code to new Survey sub-components
            if (item.data.repeat) {
                const selectedOptions = options
                    .filter((opt) => answer.includes(opt.value))
                    .map((o) => o.value);
                return (_jsx(Box, { children: displayArray.map((group, index) => {
                        return (_jsx(Grid, Object.assign({ container: true, spacing: 1 }, { children: group.map((option, idx) => {
                                const isOptionChecked = selectedOptions.includes(option.value);
                                return (_jsx(Grid, Object.assign({ item: true, xs: Math.ceil(12 / rowColumns) }, { children: _jsx(FormControlLabel, { className: classes.checkboxForm, value: option.value, control: _jsx(Checkbox, { id: item.slug.toLowerCase() + idx, checkedIcon: checkedIcon && checkedIcon(option.value), checked: isOptionChecked, icon: defaultIcon && defaultIcon(option.value), onChange: (ev) => {
                                                if (!ev.target.checked)
                                                    selectedOptions.splice(selectedOptions.indexOf(option.value), 1);
                                                const value = ev.target.checked
                                                    ? [...selectedOptions, option.value]
                                                    : selectedOptions;
                                                let asOptions = options.filter((opt) => value.includes(opt.value));
                                                const noneSelection = options.filter((opt) => value.includes(opt.value) &&
                                                    opt.label === noneValue);
                                                const isNoneSelected = noneSelection.length > 0;
                                                if (isNoneSelected)
                                                    asOptions = noneSelection;
                                                if (!asOptions.length) {
                                                    onAnswer(item, []);
                                                }
                                                else {
                                                    onAnswer(item, asOptions.map((o) => o.value));
                                                }
                                            } }), label: option.label, labelPlacement: "bottom" }) }), option.value));
                            }) }), index));
                    }) }));
            }
            else {
                const selectedOption = ((_f = options.find((opt) => answer.includes(opt.value))) === null || _f === void 0 ? void 0 : _f.value) || '';
                // If there is only one option and the question is required, auto-set the value
                if (item.required && options.length === 1 && !selectedOption) {
                    onAnswer(item, [options[0].value]);
                }
                return (_jsx(Box, { children: displayArray.map((group, index) => {
                        return (_jsx(Grid, Object.assign({ container: true, spacing: 1 }, { children: group.map((option, idx) => {
                                const isOptionChecked = selectedOptions.includes(option.value);
                                return (_jsx(Grid, Object.assign({ item: true, xs: Math.ceil(12 / rowColumns) }, { children: _jsx(FormControlLabel, { className: classes.checkboxForm, value: option.value, control: _jsx(Checkbox, { id: item.slug.toLowerCase() + idx, checkedIcon: checkedIcon && checkedIcon(option.value), checked: isOptionChecked, icon: defaultIcon && defaultIcon(option.value), onChange: (ev) => {
                                                if (!ev.target.checked)
                                                    selectedOptions.splice(selectedOptions.indexOf(option.value), 1);
                                                const value = ev.target.checked
                                                    ? option.value
                                                    : undefined;
                                                const asOption = options.find((opt) => value === opt.value);
                                                if (!asOption) {
                                                    onAnswer(item, []);
                                                }
                                                else {
                                                    onAnswer(item, [asOption.value]);
                                                }
                                            } }), label: option.label, labelPlacement: "bottom" }) }), option.value));
                            }) }), index));
                    }) }));
            }
        }
        case QuestionVariants.zipCode: {
            const asStringItem = item;
            asStringItem.data.characterLimit = '00000'.length;
            return (_jsx(SurveyStringField, { id: item.slug.toLowerCase(), className: baseClasses, item: asStringItem, answer: answer[0], onAnswer: onAnswer, fullWidth: true, name: "zipCode", type: "text", placeholder: "10001", mask: {
                    mask: MaskedRange,
                    from: 0,
                    to: 99999,
                    maxLength: 5,
                }, autoFocus: (_h = (_g = item.custom) === null || _g === void 0 ? void 0 : _g.autoFocus) !== null && _h !== void 0 ? _h : false, inputProps: {
                    inputMode: 'numeric',
                }, error: !!answer[0] && answer[0].length !== 5, helperText: !!answer[0] && answer[0].length !== 5
                    ? 'Zip Code needs to be 5 digits'
                    : '' }));
        }
        case QuestionVariants.postcode: {
            const asStringItem = item;
            asStringItem.data.characterLimit = 10;
            return (_jsx(SurveyStringField, { id: item.slug.toLowerCase(), className: baseClasses, item: asStringItem, answer: answer[0], onAnswer: onAnswer, fullWidth: true, name: "postcode", type: "text", placeholder: "SW1A 0AA", 
                // mask={{
                //   prepare: function (str) {
                //     return str.toUpperCase()
                //   },
                //   definitions: {
                //     L: /[A-Z]/,
                //   },
                //   mask: 'L0L[-]0L0', // letter-number-letter number-letter-number
                // }}
                autoFocus: (_k = (_j = item.custom) === null || _j === void 0 ? void 0 : _j.autoFocus) !== null && _k !== void 0 ? _k : false, error: !!answer[0] && answer[0].length < 6, helperText: !!answer[0] && answer[0].length < 6
                    ? 'Postcode needs to be at least 6 characters'
                    : '' }));
        }
        case QuestionVariants.postalCode: {
            const asStringItem = item;
            asStringItem.data.characterLimit = 7;
            return (_jsx(SurveyStringField, { id: item.slug.toLowerCase(), className: baseClasses, item: asStringItem, answer: answer[0], onAnswer: onAnswer, fullWidth: true, name: "postalCode", type: "text", placeholder: "HOH-0H0", mask: {
                    prepare: function (str) {
                        return str.toUpperCase();
                    },
                    definitions: {
                        L: /[A-Z]/,
                    },
                    mask: 'L0L[-]0L0', // letter-number-letter number-letter-number
                }, autoFocus: (_m = (_l = item.custom) === null || _l === void 0 ? void 0 : _l.autoFocus) !== null && _m !== void 0 ? _m : false, error: !!answer[0] && answer[0].length !== 7, helperText: !!answer[0] && answer[0].length !== 7
                    ? 'Postal Code needs to be 6 characters'
                    : '' }));
        }
        case QuestionVariants.inputDecimal:
        case QuestionVariants.inputInteger: {
            const asNum = Number(answer[0]);
            const hasAnswer = answer[0] !== undefined;
            if (hasAnswer && Number.isNaN(asNum)) {
                throw new Error(`Encountered invalid string answer for survey question variant ${item.variant}. Was: ${answer[0]}`);
            }
            return (_jsx(SurveyNumberField, { id: item.slug.toLowerCase(), className: baseClasses, item: item, 
                // Number fields are implemented with a text field so the answer needs to be a string
                answer: hasAnswer ? String(answer[0]) : undefined, autoFocus: (_p = (_o = item.custom) === null || _o === void 0 ? void 0 : _o.autoFocus) !== null && _p !== void 0 ? _p : false, onAnswer: onAnswer }));
        }
        case QuestionVariants.inputText:
        case QuestionVariants.inputString: {
            if (typeof answer[0] === 'number') {
                throw new Error('Encountered number answer for survey question variant input-string.');
            }
            return (_jsx(SurveyStringField, { id: item.slug.toLowerCase(), className: baseClasses, item: item, answer: answer[0], autoFocus: (_r = (_q = item.custom) === null || _q === void 0 ? void 0 : _q.autoFocus) !== null && _r !== void 0 ? _r : false, onAnswer: onAnswer }));
        }
        case QuestionVariants.inputTextarea: {
            if (typeof answer[0] === 'number') {
                throw new Error('Encountered number answer for survey question variant input-textarea.');
            }
            return (_jsx(SurveyStringField, { id: item.slug.toLowerCase(), className: baseClasses, item: item, answer: answer[0], autoFocus: (_t = (_s = item.custom) === null || _s === void 0 ? void 0 : _s.autoFocus) !== null && _t !== void 0 ? _t : false, onAnswer: onAnswer }));
        }
        case QuestionVariants.inputDate: {
            if (typeof answer[0] === 'number') {
                throw new Error('Encountered number answer for survey question variant input-date.');
            }
            return (_jsx(SurveyDateField, { id: item.slug.toLowerCase(), className: baseClasses, item: item, answer: answer[0], autoFocus: (_v = (_u = item.custom) === null || _u === void 0 ? void 0 : _u.autoFocus) !== null && _v !== void 0 ? _v : false, onAnswer: onAnswer }));
        }
        default:
            throw new Error(
            //The item is now known to be unknown. Allow the `any`
            // eslint-disable-next-line
            `Unknown QuestionVariant ${item.variant}.`);
    }
};
export default SurveyItemQuestion;
